import { useEffect, useState } from 'react';
import {
  RotateCcw,
  Save,
  Moon,
  Sun,
  Palette,
  ChevronDown,
  Check,
  Lock,
  Unlock,
  ChevronRight,
  Eye,
  Paintbrush,
} from 'lucide-react';
import { useResetTheme, useSaveTheme, usePlatformTheme } from '@/hooks/useTheme';
import { useConfirm } from '@/components/ConfirmDialog';
import { applyTheme, COLOR_TOKEN_GROUPS, type Theme } from '@/lib/theme';
import {
  ACCENT_PRESETS,
  BASE_PRESETS,
  composeTheme,
  DEFAULT_PRESETS,
  detectPresetIds,
  FONT_CHOICES,
  STYLE_PRESETS,
  applyHeadingFont,
  loadPresetFonts,
  type PresetIds,
  type FontPresetEntry,
} from '@/lib/theme-presets';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type PreviewMode = 'light' | 'dark';

export function ThemeSettingsPage() {
  const { data, isPending } = usePlatformTheme();
  const save = useSaveTheme();
  const reset = useResetTheme();
  const confirm = useConfirm();

  const [ids, setIds] = useState<PresetIds>(DEFAULT_PRESETS);
  const [chartLinked, setChartLinked] = useState(true);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [overridesDark, setOverridesDark] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<PreviewMode>('light');
  const [dirty, setDirty] = useState(false);
  const [initialised, setInitialised] = useState(false);

  // Seed from server
  useEffect(() => {
    if (!data || initialised) return;
    const detected = detectPresetIds(data.theme);
    setIds(detected);
    setInitialised(true);
  }, [data, initialised]);

  // Compose theme on every change
  const theme: Theme = composeTheme(ids, overrides, overridesDark);

  // Live preview
  useEffect(() => {
    applyTheme(theme);
    loadPresetFonts(ids);
    applyHeadingFont(ids);
  }, [theme, ids]);

  // Mirror preview-mode toggle to <html> class (without persisting)
  useEffect(() => {
    const wasDark = document.documentElement.classList.contains('dark');
    if (mode === 'dark' && !wasDark) document.documentElement.classList.add('dark');
    if (mode === 'light' && wasDark) document.documentElement.classList.remove('dark');
  }, [mode]);

  if (isPending) {
    return <div className="text-muted-foreground">Yükleniyor…</div>;
  }

  const updateId = (key: keyof PresetIds, value: string) => {
    setIds((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const updateOverride = (m: PreviewMode, key: string, value: string) => {
    if (m === 'light') setOverrides((o) => ({ ...o, [key]: value }));
    else setOverridesDark((o) => ({ ...o, [key]: value }));
    setDirty(true);
  };

  const clearOverride = (m: PreviewMode, key: string) => {
    if (m === 'light') {
      setOverrides((o) => {
        const next = { ...o };
        delete next[key];
        return next;
      });
    } else {
      setOverridesDark((o) => {
        const next = { ...o };
        delete next[key];
        return next;
      });
    }
    setDirty(true);
  };

  const onSave = async () => {
    await save.mutateAsync(theme);
    setDirty(false);
  };

  const onReset = async () => {
    const ok = await confirm({
      title: 'Temayı sıfırla?',
      description: 'Tüm özel ayarların kaldırılır ve varsayılan tema uygulanır.',
      confirmText: 'Sıfırla',
      danger: true,
    });
    if (!ok) return;
    const res = await reset.mutateAsync();
    const detected = detectPresetIds(res.theme);
    setIds(detected);
    setOverrides({});
    setOverridesDark({});
    setDirty(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Palette className="w-5 h-5" /> Tema
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Stil, renk ve yazı tipi presetlerini seç. Gelişmiş ayarlardan ince ayar yap.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border p-0.5">
            <Button
              type="button"
              variant={mode === 'light' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setMode('light')}
            >
              <Sun className="w-4 h-4" /> Light
            </Button>
            <Button
              type="button"
              variant={mode === 'dark' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setMode('dark')}
            >
              <Moon className="w-4 h-4" /> Dark
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={onReset} disabled={reset.isPending}>
            <RotateCcw className="w-4 h-4" /> Sıfırla
          </Button>
          <Button size="sm" onClick={onSave} disabled={!dirty || save.isPending}>
            <Save className="w-4 h-4" />{' '}
            {save.isPending ? 'Kaydediliyor…' : dirty ? 'Kaydet' : 'Kaydedildi'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        {/* LEFT: preset pickers */}
        <div className="space-y-3 lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-1">
          <PresetSlot
            label="Style"
            value={STYLE_PRESETS.find((p) => p.id === ids.style) ?? STYLE_PRESETS[1]}
            options={STYLE_PRESETS}
            onChange={(id) => updateId('style', id)}
            renderAdornment={(p) => (
              <div
                className="h-5 w-5 border-2 border-current"
                style={{ borderRadius: p.radius }}
                aria-hidden
              />
            )}
          />

          <PresetSlot
            label="Base Color"
            value={BASE_PRESETS.find((p) => p.id === ids.base) ?? BASE_PRESETS[1]}
            options={BASE_PRESETS}
            onChange={(id) => {
              updateId('base', id);
              if (chartLinked) {
                // chart is linked to base by default — visual only, no token change
              }
            }}
            renderAdornment={(p) => <ColorDot color={p.swatch} />}
          />

          <PresetSlot
            label="Theme"
            value={ACCENT_PRESETS.find((p) => p.id === ids.accent) ?? ACCENT_PRESETS[0]}
            options={ACCENT_PRESETS}
            onChange={(id) => updateId('accent', id)}
            renderAdornment={(p) => <ColorDot color={p.swatch} />}
          />

          <PresetSlot
            label="Chart Color"
            value={BASE_PRESETS.find((p) => p.id === ids.base) ?? BASE_PRESETS[1]}
            options={BASE_PRESETS}
            onChange={(id) => updateId('base', id)}
            disabled={chartLinked}
            renderAdornment={(p) => <ColorDot color={p.swatch} />}
            leadingIcon={
              <button
                type="button"
                onClick={() => setChartLinked((v) => !v)}
                className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                title={chartLinked ? 'Base Color ile kilitli' : 'Bağımsız'}
              >
                {chartLinked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
              </button>
            }
          />

          <PresetSlot
            label="Heading"
            value={FONT_CHOICES.find((p) => p.id === ids.heading) ?? FONT_CHOICES[0]}
            options={FONT_CHOICES}
            onChange={(id) => updateId('heading', id)}
            renderAdornment={(p) => <FontAa font={p} />}
          />

          <PresetSlot
            label="Font"
            value={FONT_CHOICES.find((p) => p.id === ids.body) ?? FONT_CHOICES[0]}
            options={FONT_CHOICES}
            onChange={(id) => updateId('body', id)}
            renderAdornment={(p) => <FontAa font={p} />}
          />

          <AdvancedSection
            mode={mode}
            theme={theme}
            overrides={overrides}
            overridesDark={overridesDark}
            onOverride={updateOverride}
            onClear={clearOverride}
          />
        </div>

        {/* RIGHT: live preview */}
        <PreviewPanel theme={theme} />
      </div>
    </div>
  );
}

// ---------- Preset picker card ----------

type Labelable = { id: string; label: string };

function PresetSlot<T extends Labelable>({
  label,
  value,
  options,
  onChange,
  renderAdornment,
  leadingIcon,
  disabled,
}: {
  label: string;
  value: T;
  options: T[];
  onChange: (id: string) => void;
  renderAdornment?: (p: T) => React.ReactNode;
  leadingIcon?: React.ReactNode;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={cn(
          'w-full text-left rounded-lg border bg-card px-3 py-2.5 flex items-center gap-3 transition-colors',
          disabled ? 'opacity-60' : 'hover:bg-accent',
        )}
      >
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-sm font-medium truncate">{value.label}</div>
        </div>
        {leadingIcon}
        {renderAdornment?.(value)}
        <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>
      {open && !disabled && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 mt-1 z-50 rounded-lg border bg-popover shadow-lg overflow-hidden">
            <div className="py-1 max-h-80 overflow-auto">
              {options.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    onChange(opt.id);
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-accent flex items-center gap-3"
                >
                  {renderAdornment?.(opt)}
                  <span className="text-sm flex-1 truncate">{opt.label}</span>
                  {opt.id === value.id && <Check className="w-4 h-4 text-primary" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ColorDot({ color }: { color: string }) {
  return (
    <span
      className="inline-block h-5 w-5 rounded-full border shrink-0"
      style={{ backgroundColor: color }}
      aria-hidden
    />
  );
}

function FontAa({ font }: { font: FontPresetEntry }) {
  return (
    <span
      className="inline-block text-lg leading-none shrink-0"
      style={{ fontFamily: font.stack }}
      aria-hidden
    >
      Aa
    </span>
  );
}

// ---------- Advanced overrides ----------

function AdvancedSection({
  mode,
  theme,
  overrides,
  overridesDark,
  onOverride,
  onClear,
}: {
  mode: PreviewMode;
  theme: Theme;
  overrides: Record<string, string>;
  overridesDark: Record<string, string>;
  onOverride: (m: PreviewMode, key: string, value: string) => void;
  onClear: (m: PreviewMode, key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const currentOverrides = mode === 'light' ? overrides : overridesDark;

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-4 py-3 flex items-center gap-2 hover:bg-accent/50 rounded-t-lg"
      >
        <Paintbrush className="w-4 h-4 text-muted-foreground" />
        <span className="flex-1 text-sm font-medium">Gelişmiş Ayarlar</span>
        <ChevronRight className={cn('w-4 h-4 text-muted-foreground transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <CardContent className="pt-0 space-y-3 border-t">
          <p className="text-xs text-muted-foreground pt-3">
            Tek tek token override'ları. Boşaltmak için "Kaldır".
          </p>
          {COLOR_TOKEN_GROUPS.map((group) => (
            <div key={group.label} className="space-y-1.5">
              <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider pt-1">
                {group.label}
              </div>
              {group.keys.map((key) => {
                const value = theme[mode][key] ?? '';
                const overridden = key in currentOverrides;
                const isHex = /^#[0-9a-fA-F]+$/.test(value);
                return (
                  <div key={key} className="flex items-center gap-1.5">
                    <div
                      className="text-[10px] text-muted-foreground w-28 shrink-0 font-mono truncate"
                      title={key}
                    >
                      --{key}
                    </div>
                    <input
                      type="color"
                      value={isHex ? value : '#000000'}
                      onChange={(e) => onOverride(mode, key, e.target.value)}
                      className="h-7 w-7 rounded cursor-pointer border shrink-0"
                      disabled={!isHex}
                    />
                    <Input
                      value={value}
                      onChange={(e) => onOverride(mode, key, e.target.value)}
                      className={cn(
                        'h-7 font-mono text-[10px]',
                        overridden && 'ring-1 ring-primary',
                      )}
                    />
                    {overridden && (
                      <button
                        type="button"
                        onClick={() => onClear(mode, key)}
                        className="text-[10px] text-muted-foreground hover:text-destructive px-1"
                        title="Override'ı kaldır"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

// ---------- Right side live preview ----------

function PreviewPanel({ theme }: { theme: Theme }) {
  void theme; // used via CSS vars
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Eye className="w-4 h-4" /> Önizleme
        </CardTitle>
        <CardDescription>
          Canlı — kaydedince tüm kullanıcılar için aktif olur.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">Butonlar</h3>
          <div className="flex flex-wrap gap-2">
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="link">Link</Button>
          </div>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">Kartlar</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Toplam Kullanıcı</CardTitle>
                <CardDescription>Son 30 gün</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold">2.847</div>
                <p className="text-xs text-muted-foreground mt-1">+%12.4 geçen aya göre</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Gelir</CardTitle>
                <CardDescription>MRR</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold">₺84.320</div>
                <p className="text-xs text-muted-foreground mt-1">+%8.1 geçen aya göre</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">Tipografi</h3>
          <div className="space-y-1">
            <h1 className="text-3xl font-bold">Ana başlık — H1</h1>
            <h2 className="text-2xl font-semibold">Alt başlık — H2</h2>
            <h3 className="text-xl font-semibold">Bölüm başlığı — H3</h3>
            <p className="text-base">
              Bu bir paragraf örneğidir.{' '}
              <a href="#" className="text-primary underline-offset-4 hover:underline">
                Bir link örneği
              </a>
              .
            </p>
            <p className="text-sm text-muted-foreground">Muted metin — ikincil bilgi.</p>
          </div>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">Durumlar</h3>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              Info
            </span>
            <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              Başarılı
            </span>
            <span className="inline-flex items-center rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive">
              Hata
            </span>
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              Draft
            </span>
          </div>
        </section>
      </CardContent>
    </Card>
  );
}
